import React, { useEffect, useState } from 'react';
import {
  PiCheckCircle, PiClock, PiTruck, PiShieldCheck, PiArrowsClockwise, PiLightning,
} from 'react-icons/pi';
import {
  PreviewFrame, Btn, TrustBar,
} from './_shared';

// Legacy fallbacks for pages saved before these fields existed (undefined → default, [] → hidden)
const DEFAULT_TRUST_ITEMS = ['Free shipping over $50', 'Free 30-day returns', 'Secure checkout', 'Ships in 24h'];
const TRUST_ICONS = [PiTruck, PiArrowsClockwise, PiShieldCheck, PiLightning];
const DEFAULT_BENEFITS = ['Exclusive savings', 'Free shipping over $50', '30-day money-back guarantee'];

// Time remaining until endDate — null when empty/invalid/past (the countdown hides itself)
function getTimeLeft(endDate) {
  if (!endDate) return null;
  const end = new Date(endDate).getTime();
  if (Number.isNaN(end)) return null;
  const diff = end - Date.now();
  if (diff <= 0) return null;
  const totalSecs = Math.floor(diff / 1000);
  return {
    days: Math.floor(totalSecs / 86400),
    hours: Math.floor((totalSecs % 86400) / 3600),
    mins: Math.floor((totalSecs % 3600) / 60),
    secs: totalSecs % 60,
  };
}

export default function CampaignPromoTemplate({ sections, entity, viewport, products = [], theme, editable, onSectionClick }) {
  const hero = sections.hero || {};
  const countdown = sections.countdown || {};
  const productsSection = sections.products || {};
  const benefits = sections.benefits || {};
  const finalCta = sections.finalCta || {};
  const accent = theme?.primaryColor || '#FFC857';

  // Real ticking countdown driven by countdown.endDate (replaces the old static fake digits)
  const [timeLeft, setTimeLeft] = useState(() => getTimeLeft(countdown.endDate));
  useEffect(() => {
    setTimeLeft(getTimeLeft(countdown.endDate));
    if (!countdown.endDate) return undefined;
    const timer = setInterval(() => setTimeLeft(getTimeLeft(countdown.endDate)), 1000);
    return () => clearInterval(timer);
  }, [countdown.endDate]);

  const featured = (productsSection.productIds || []).map((id) => products.find((p) => p.id === id)).filter(Boolean);
  const display = featured.length ? featured : products.slice(0, productsSection.columns || 3);
  const cols = Math.max(1, Math.min(4, Math.round(productsSection.columns) || 3));

  const announcementText = hero.discountCallout ?? '';
  const heroEyebrow = hero.eyebrow ?? '⚡ Limited time offer';
  const heroFootnote = hero.footnote ?? 'Free shipping over $50';
  const trustItems = Array.isArray(productsSection.trustItems) ? productsSection.trustItems : DEFAULT_TRUST_ITEMS;
  const productsEyebrow = productsSection.eyebrow ?? 'SHOP THE SALE';
  const topPickBadge = productsSection.topPickBadge ?? 'TOP PICK';
  const benefitItems = Array.isArray(benefits.items) ? benefits.items : DEFAULT_BENEFITS;
  const finalCtaEyebrow = finalCta.eyebrow ?? "⏰ DON'T WAIT";
  const finalCtaFootnote = finalCta.footnote ?? '🔒 Secure checkout · Free returns · Ships in 24 hours';
  // Countdown unit labels — per-index fallback so a short/legacy list keeps the defaults.
  const unitLabels = Array.isArray(countdown.unitLabels) ? countdown.unitLabels : [];

  return (
    <PreviewFrame viewport={viewport} accent={accent} theme={theme} sections={sections} editable={editable} onSectionClick={onSectionClick}>
      {/* ANNOUNCEMENT BAR — hidden when toggled off or the callout is empty */}
      {hero.showAnnouncement !== false && announcementText && (
        <div data-lp-section="hero" style={{
          background: accent, color: '#1F1B33', textAlign: 'center',
          padding: '10px 16px', fontSize: 13, fontWeight: 700, letterSpacing: 0.3,
          display: 'flex', justifyContent: 'center', gap: 8, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <PiLightning size={14} weight="fill" />
          <span data-lp-field="discountCallout">{announcementText}</span>
        </div>
      )}

      {/* HERO — bold, full-bleed, urgent */}
      <section data-lp-section="hero" style={{
        background: hero.heroImageUrl
          ? `linear-gradient(135deg, rgba(31,27,51,0.78) 0%, rgba(45,40,73,0.72) 100%), url(${hero.heroImageUrl}) center/cover`
          : 'linear-gradient(135deg, #1F1B33 0%, #2D2849 100%)',
        color: '#fff',
        padding: viewport === 'mobile' ? '56px 24px' : '88px 48px 64px',
        textAlign: 'center',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Decorative accent */}
        <div style={{ position: 'absolute', top: -100, right: -100, width: 300, height: 300, borderRadius: '50%', background: accent, opacity: 0.08 }} />
        <div style={{ position: 'absolute', bottom: -80, left: -80, width: 240, height: 240, borderRadius: '50%', background: accent, opacity: 0.06 }} />

        <div style={{ position: 'relative', maxWidth: 800, margin: '0 auto' }}>
          {heroEyebrow && (
            <div data-lp-field="eyebrow" style={{
              display: 'inline-block', background: accent, color: '#1F1B33',
              padding: '8px 16px', borderRadius: 4, fontSize: 12, fontWeight: 800,
              marginBottom: 24, letterSpacing: 1.5, textTransform: 'uppercase',
            }}>
              {heroEyebrow}
            </div>
          )}
          <h1 style={{ fontSize: viewport === 'mobile' ? 40 : 60, fontWeight: 900, margin: '0 0 20px', lineHeight: 1, letterSpacing: -1.5 }}>
            {hero.headline || 'Limited time offer'}
          </h1>
          <p style={{ fontSize: 18, opacity: 0.9, margin: '0 0 32px', maxWidth: 600, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>
            {hero.subheadline || 'Shop the collection now — discounts this big don\'t come around often.'}
          </p>
          <Btn big color={accent} textColor="#1F1B33">{hero.ctaLabel || 'Shop the sale →'}</Btn>
          {heroFootnote && (
            <div data-lp-field="footnote" style={{ marginTop: 16, fontSize: 13, opacity: 0.7 }}>
              {heroFootnote}
            </div>
          )}
        </div>
      </section>

      {/* COUNTDOWN — real ticking timer; hidden when endDate is empty or already past */}
      {countdown.enabled && timeLeft && (
        <section data-lp-section="countdown" style={{
          background: '#0F0C1F', color: '#fff', padding: '32px 24px', textAlign: 'center',
          borderBottom: `3px solid ${accent}`,
        }}>
          <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 16, display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>
            <PiClock size={16} /> {countdown.headline || 'Sale ends in'}
          </div>
          <div style={{ display: 'inline-flex', gap: 12, justifyContent: 'center' }}>
            {[[timeLeft.days, unitLabels[0] ?? 'Days'], [timeLeft.hours, unitLabels[1] ?? 'Hours'], [timeLeft.mins, unitLabels[2] ?? 'Min'], [timeLeft.secs, unitLabels[3] ?? 'Sec']].map(([n, l], i) => (
              <React.Fragment key={i}>
                <div style={{
                  background: 'linear-gradient(180deg, #2D2849 0%, #1F1B33 100%)',
                  borderRadius: 8, padding: '14px 18px', minWidth: 72,
                  border: '1px solid rgba(255,200,87,0.2)',
                }}>
                  <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1, color: accent, fontVariantNumeric: 'tabular-nums' }}>{String(n).padStart(2, '0')}</div>
                  <div style={{ fontSize: 10, opacity: 0.7, marginTop: 6, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>{l}</div>
                </div>
                {i < 3 && <div style={{ alignSelf: 'center', fontSize: 22, opacity: 0.4, fontWeight: 800 }}>:</div>}
              </React.Fragment>
            ))}
          </div>
        </section>
      )}

      {/* TRUST BAR — hidden when the merchant empties the list */}
      {trustItems.length > 0 && (
        <div data-lp-section="products">
          <TrustBar items={trustItems.map((label, i) => [TRUST_ICONS[i] || PiCheckCircle, label])} />
        </div>
      )}

      {/* PRODUCTS */}
      <section data-lp-section="products" style={{ padding: viewport === 'mobile' ? '48px 16px' : '72px 32px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          {productsEyebrow && (
            <div data-lp-field="eyebrow" style={{ fontSize: 12, color: '#7d7a86', fontWeight: 700, letterSpacing: 1.5, marginBottom: 8 }}>{productsEyebrow}</div>
          )}
          <h2 style={{ fontSize: 32, fontWeight: 800, margin: 0, letterSpacing: -0.5 }}>
            {productsSection.headline || 'Featured products'}
          </h2>
        </div>
        <div style={{
          display: 'grid', gap: 20,
          gridTemplateColumns: viewport === 'mobile' ? '1fr 1fr' : `repeat(${cols}, 1fr)`,
        }}>
          {/* Placeholder skeleton cards only appear in the editor preview — live pages render nothing when no products resolve */}
          {(display.length ? display : (editable ? Array.from({ length: cols }) : [])).map((p, i) => (
            <div key={p?.id || i} style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #E8E5EE', position: 'relative', transition: 'transform 0.2s' }}>
              {i === 0 && topPickBadge && (
                <div data-lp-field="topPickBadge" style={{ position: 'absolute', top: 12, right: 12, background: '#1F1B33', color: '#fff', fontSize: 10, fontWeight: 700, padding: '4px 8px', borderRadius: 4, zIndex: 1 }}>
                  {topPickBadge}
                </div>
              )}
              <div style={{ aspectRatio: '1/1', background: '#F2EEFF',
                backgroundImage: p?.imageUrl ? `url(${p.imageUrl})` : undefined,
                backgroundSize: 'cover', backgroundPosition: 'center' }} />
              <div style={{ padding: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 600, margin: '4px 0' }}>{p?.title || 'Product name'}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
                  <span style={{ fontSize: 16, color: '#1F1B33', fontWeight: 800 }}>${(p?.price ?? 0).toFixed(2)}</span>
                </div>
                <Btn full color="#1F1B33"><span data-lp-field="ctaLabel">{productsSection.ctaLabel || 'Add to cart'}</span></Btn>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* BENEFITS — hidden when the merchant empties the list */}
      {benefitItems.length > 0 && (
        <section data-lp-section="benefits" style={{ padding: viewport === 'mobile' ? '48px 24px' : '64px 32px', background: '#FAFAFC' }}>
          <div style={{ maxWidth: 1000, margin: '0 auto' }}>
            <h2 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 32px', textAlign: 'center', letterSpacing: -0.3 }}>
              {benefits.headline || 'Why shop this collection'}
            </h2>
            <div style={{ display: 'grid', gap: 16, gridTemplateColumns: viewport === 'mobile' ? '1fr' : `repeat(${Math.min(3, benefitItems.length)}, 1fr)` }}>
              {benefitItems.map((it, i) => (
                <div key={i} style={{ background: '#fff', borderRadius: 12, padding: 24, textAlign: 'center', border: '1px solid #ECE8F5' }}>
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#FFF6E0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                    <PiCheckCircle size={26} style={{ color: '#1F1B33' }} weight="fill" />
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.4 }}>{it}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section data-lp-section="finalCta" style={{ padding: viewport === 'mobile' ? '64px 24px' : '88px 32px', background: '#1F1B33', color: '#fff', textAlign: 'center' }}>
        {finalCtaEyebrow && (
          <div data-lp-field="eyebrow" style={{ fontSize: 12, color: accent, fontWeight: 800, letterSpacing: 2, marginBottom: 12 }}>{finalCtaEyebrow}</div>
        )}
        <h2 style={{ fontSize: viewport === 'mobile' ? 32 : 44, fontWeight: 900, margin: '0 0 12px', letterSpacing: -0.8 }}>
          {finalCta.headline || "Don't miss out"}
        </h2>
        {finalCta.urgencyText && (
          <p data-lp-field="urgencyText" style={{ fontSize: 14, color: accent, fontWeight: 700, margin: '0 0 24px', letterSpacing: 0.5 }}>{finalCta.urgencyText}</p>
        )}
        <Btn big color={accent} textColor="#1F1B33">{finalCta.ctaLabel || 'Shop the sale now →'}</Btn>
        {finalCtaFootnote && (
          <div data-lp-field="footnote" style={{ marginTop: 20, fontSize: 12, opacity: 0.6 }}>
            {finalCtaFootnote}
          </div>
        )}
      </section>
    </PreviewFrame>
  );
}
